/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2017, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'../../../DynamicFileLoader',
	'underscore'
], (DynamicFileLoader, _) => {
	'use strict';

	return class SourceManager {

		constructor(options) {
			this.sourceModel = options.sourceModel;
			this.dashboardApi = options.dashboardApi;

			this.sourcesCollectionManager = options.sourcesCollectionManager;

			// If the source was just added then we should already have the localized name
			if (this.sourceModel) {
				this.localizedName = this.sourceModel.get('localizedName') || null;
				this.cmObjectExists = this.sourceModel.get('cmObjectExists') || undefined;
				this.modificationTime = this.sourceModel.get('modificationTime') || null;
				this.sourceModel.setAjaxSvc(this.dashboardApi.getGlassCoreSvc('.Ajax'));
				this.sourceModel.setUserProfileSvc(this.dashboardApi.getGlassCoreSvc('.UserProfile'));
			}

			this._loadMetadataProgressHandlers = [];
		}

		destroy() {
			this._moduleAPI = null;
			this._clearLoadMetadataProgressHandlers();
		}

		/**
		 * Returns a promise which will resolve with a module API wrapper around the moserJS module object
		 * @param moduleID optional moduleID that already exists on the server
		 * @param {boolean} [showErrorToast] optional flag for showing error toast
		 * @param {boolean} [clearTemporaryModule] optional flag to clear temporary module (e.g. when relinking)
		 * @return {Promise} Promise that will get resolved with the ModuleAPI object
		 */
		getModule(moduleId, showErrorToast, clearTemporaryModule) {
			if (this._moduleAPI) {
				if (this.isInErrorState()) {
					return Promise.reject({
						sourceInfo: {
							name: this.localizedName
						}
					});
				}
				if (clearTemporaryModule) {
					this._moduleAPI.clearTemporaryModule();
				}
				// wait for the temporary module to be loaded
				// which can take longer without optimizations
				return this._moduleAPI.whenTemporaryModuleReady().then(() => {
					return this._moduleAPI;
				});
			}

			//if the load module is in progress, return the current promise instead of creating another module.
			if (this.modulePromise) {
				return this.modulePromise;
			}

			// Delay loading this class as long as possible since it loads the modelling code
			this.modulePromise = DynamicFileLoader.load(['dashboard-analytics/dataSources/modelapis/ModuleAPI'])
				.then(this._loadModuleAPI.bind(this, moduleId, showErrorToast))
				.then((res) => {
					this.modulePromise = null;
					this.setState('ready', true);
					return res;
				}).catch((error) => {
					this.dashboardApi.getGlassCoreSvc('.Logger').error(error);
					this.setState('error', true);
					this.modulePromise = null;
					throw error;
				}).finally(this._clearLoadMetadataProgressHandlers.bind(this));

			return this.modulePromise;
		}

		_loadModuleAPI(moduleId, showErrorToast, modules) {
			const ModuleAPI = modules[0];
			const moduleAPI = new ModuleAPI({
				'sourceModel': this.sourceModel,
				'dashboardApi': this.dashboardApi,
				'sourceModelManager': this
			});
			// Make sure we call load before resolving the promise.  The load will take care of making
			// sure the moserJS object is initialzed with the metadata
			return moduleAPI.load(moduleId, showErrorToast).then(() => {
				this._moduleAPI = moduleAPI;
				return this._moduleAPI;
			}).catch((error) => {
				this.dashboardApi.getGlassCoreSvc('.Logger').error(error);
				this.setState('error', true);
				this.setErrorCode(error.code, true);
				this._moduleAPI = moduleAPI;
				throw error;
			});
		}

		/**
		 * Returns the type of the intiial source
		 * @return {String} The type of the source
		 */
		getType() {
			return this.sourceModel.get('type');
		}

		registerLoadMetadataProgressHandler(handler) {
			this._loadMetadataProgressHandlers.push(handler);
			if(this._lastLoadMetadataProgressUpdate) {
				handler(this._lastLoadMetadataProgressUpdate);
			}
		}

		_clearLoadMetadataProgressHandlers() {
			this._loadMetadataProgressHandlers = [];
			delete this._lastLoadMetadataProgressUpdate;
		}

		onLoadMetadataProgress(progressInfo = {}) {
			if(progressInfo.percentage === 100) {
				// Dashboard still relies on the module load resolved promise to identify the finished state.
				// MUI claims 100% prematurely, i.e after the module POST is done, but before the GET.
				return;
			}
			this._lastLoadMetadataProgressUpdate = {
				percentage: progressInfo.percentage
			};
			this._loadMetadataProgressHandlers.forEach((handler) => {
				handler(this._lastLoadMetadataProgressUpdate);
			});
		}

		_queryCMObject() {
			if (this.hasQueriedCM) {
				return Promise.resolve();
			}

			if (!this._queryCMObjectPromise) {
				// Should query sourceModel only once.
				this._queryCMObjectPromise = this.dashboardApi.getGlassCoreSvc('.Ajax')
					.ajax({
						url: 'v1/objects/' + this.sourceModel.get('assetId') + '?fields=userInterfaces,defaultName,searchPath',
						type: 'GET',
						headers: {
							'Accept': 'application/json'
						}
					})
					.then(this._handleQueryCMObjectSuccess.bind(this))
					.catch(this._handleQueryCMObjectError.bind(this));
			}
			return this._queryCMObjectPromise;
		}

		_handleQueryCMObjectSuccess(response) {
			const {
				data
			} = response.data;

			this.hasQueriedCM = true;
			if (data && data.length > 0) {
				this.localizedName = data[0].defaultName;
				this.modificationTime = data[0].modificationTime;
				this.sourceModel.searchPath = data[0].searchPath;
				if (data[0].type === 'package' && data[0].userInterfaces &&
					_.indexOf(data[0].userInterfaces, 'analysisStudio') >= 0) {
					this.sourceModel.isOlapPackage = true;
				} else {
					this.sourceModel.isOlapPackage = false;
				}

				this.cmObjectExists = true;
			} else {
				this._setErrorState();
			}
			if (this.sourceModel.get('pinSourceName')) {
				this.localizedName = this.localizedName + ' - ' + this.sourceModel.get('pinSourceName');
			}
		}

		_handleQueryCMObjectError() {
			this._setErrorState();
		}

		_setErrorState() {
			this.localizedName = this.sourceModel.get('name');
			this.cmObjectExists = false;
			this.setState('error', true);
		}

		/**
		 * This check could be replaced with a pluggable provider so that we don't leave 
		 * any CA specific code under dashboard-livewidget/js. However...
		 * Ideally, MUI/Moser, as "owners" of the datasource, would provide an API to us to retrieve the datasource information
		 * which would be agnostic of the storage solution (e.g CM vs CAMS). If that was the case,
		 * this would be a single path of code anyway, so not worrying with the pluggable provider for now.
		 */
		_isCMDatasourceType() {
			const type = this.getType();
			const CMSupportedTypes = ['module', 'dataSet2', 'uploadedFile', 'package'];
			return CMSupportedTypes.includes(type);
		}

		/**
		 * Get the localized name of the source
		 * @return {Promise} Promise that will be resolved with localized name of the data source
		 */
		getLocalizedName() {
			if (this.localizedName) {
				return Promise.resolve(this.localizedName);
			} else if (this._isCMDatasourceType()) {
				return this._queryCMObject()
					.then(() => this.localizedName);
			} else {
				this.localizedName = this.sourceModel.get('name');
				return Promise.resolve(this.localizedName);
			}
		}

		getSearchPath() {
			if (this.sourceModel.searchPath) {
				return Promise.resolve(this.sourceModel.searchPath);
			} else if (this._isCMDatasourceType()) {
				return this._queryCMObject()
					.then(() => this.sourceModel.searchPath);
			} else {
				return Promise.resolve(this.sourceModel.assetId);
			}
		}

		exists() {
			if(this._isCMDatasourceType()) {
				if (this.cmObjectExists !== undefined) {
					return Promise.resolve(this.cmObjectExists);
				}
				return this._queryCMObject()
					.then(() => this.cmObjectExists);
			}
			return Promise.resolve(true);
		}

		getModificationTime() {
			if (this.modificationTime) {
				return Promise.resolve(this.modificationTime);
			} else if (this._isCMDatasourceType()) {
				return this._queryCMObject()
					.then(() => this.modificationTime);
			} else {
				return Promise.resolve(null);
			}
		}

		relink(newDatasourceInfo) {
			delete this.localizedName;
			delete this.modificationTime;
			delete this.cmObjectExists;
			this.hasQueriedCM = false;
			this.setState('', true);
			this.setErrorCode('', true);
			this.sourceModel.set(newDatasourceInfo, {
				payloadData: {
					skipUndoRedo: true
				}
			});

			return this.getModule(null, null, true)
				.then((moduleAPI) => moduleAPI.relink(this.sourceModel))
				.then((payload) => payload)
				.catch(this.generalRequestErrorHandler.bind(this))
				.finally(this._clearLoadMetadataProgressHandlers.bind(this));
		}

		reloadMetadata() {
			if (!this._moduleAPI) {
				return Promise.resolve();
			}

			return this._moduleAPI.reloadMetadata()
				.catch(this.generalRequestErrorHandler.bind(this));
		}

		/*
		 * A generic error handler that sets the state in the source model and re-throws the error
		 */
		generalRequestErrorHandler(error) {
			this.setState('error');
			throw error;
		}

		getState() {
			return this._state || 'loading';
		}

		setState(state, silent = false) {
			// Store the state in both the manager and the model.
			// sourceModel is shared between multiple instances of the DataSourcesService.
			// which can occur when an upgrade creates an temporary instance of the DataSourcesService.
			// The actual service may end up referring to the wrong state that was set by the upgrade.
			this._state = state;
			this.sourceModel.set({
				state
			}, silent ? {
				silent: true
			} : {});
		}

		setErrorCode(errorCode, silent = false) {
			this.sourceModel.set({
				errorCode
			}, silent ? {
				silent: true
			} : {});
		}

		getLastModified() {
			return this._lastModified;
		}

		setLastModified(lastModified) {
			this._lastModified = lastModified;
		}

		isInErrorState() {
			return this._state === 'error';
		}
	};
});
